/**
 * Belegerfassung und Belegansicht.
 *
 * Solange der Beleg ein Entwurf ist, laesst sich alles aendern. Nach dem
 * Festschreiben ist die Seite nur noch eine Ansicht - mit den Aktionen
 * Drucken, Folgebeleg und Storno. Diese Umschaltung ist der sichtbarste
 * Teil der GoBD-Anforderungen im Programm.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  calculateTotals,
  contactDisplayName,
  DOCUMENT_LIFECYCLE_LABELS,
  formatAmount,
  formatDate,
  formatPrice,
  getDocumentType,
  lineAmount,
  TAX_CODES,
  today,
  unitList,
  type DocumentType,
  formatQuantity,
} from '@erp/shared';

import { api, ApiError, apiUrl, withQuery } from '../api/client';
import type { ListResponse, Partner, PaymentTerm } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { DocumentAttachments } from '../components/DocumentAttachments';
import { type ChainEntry } from '../components/DocumentChain';
import { CreditGauge } from '../components/CreditGauge';
import { DocumentMailDialog, type DocumentMailInfo } from '../components/DocumentMailDialog';
import { ItemPicker } from '../components/ItemPicker';
import { SearchSelect } from '../components/SearchSelect';
import { ShipmentCard } from '../components/ShipmentCard';
import { DispatchJournalCard } from '../components/DispatchJournalCard';
import { UserSelect } from '../components/UserSelect';
import type { PartnerAddress } from '../components/PartnerAddresses';
import { useToast } from '../components/toast';
import { confirmLeave, useUnsavedChanges } from '../components/useUnsavedChanges';
import {
  Alert,
  Badge,
  Dialog,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';
import { callOffPercent, DOCUMENT_ROUTES, FRAMEWORK_ROUTE, StatusBadge } from './DocumentsPage';

type LineForm = {
  key: string;
  id?: string;
  lineType: 'item' | 'text' | 'subtotal';
  itemId: string | null;
  itemNumber: string | null;
  description: string;
  descriptionLong: string;
  /** Lagerplatz, Vorschlag aus dem Artikelstamm fuer das Lager des Belegs. */
  storageBin: string;
  /** Chargen je Position: erfasst (Wareneingang) oder gewaehlte Zugaenge (Lieferschein). */
  lots: LotForm[];
  /** Lieferschein: Auswahl von Hand geaendert - der Vorschlag ueberschreibt sie dann nicht mehr. */
  lotsManual?: boolean;
  /** Chargenfuehrung und Entnahmeregel des Artikels, nachgeladen; null = noch unbekannt. */
  tracking: { mode: 'none' | 'batch' | 'serial'; hasExpiry: boolean; strategy: 'fifo' | 'fefo' | 'manual' } | null;
  /**
   * Einstandspreis je Einheit (Zehntausendstel Euro) fuer den Rohertrag -
   * kommt nur mit, wenn der Benutzer das Recht "Rohertrag sehen" hat.
   */
  costPrice?: number;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPercent: number;
  taxCode: string;
  sourceLineId: string | null;
  deliveredQuantity: number;
  invoicedQuantity: number;
  /** Rahmenvertrag: bereits abgerufene Menge. */
  calledOffQuantity: number;
  /** Auftrag: reservierte Menge. */
  reservedQuantity: number;
};

type LotForm = {
  key: string;
  lotId: string | null;
  lotNumber: string;
  expiryDate: string;
  quantity: number;
};

type HeaderForm = {
  partnerId: string;
  contactId: string;
  documentDate: string;
  serviceDate: string;
  validUntil: string;
  paymentTermId: string;
  discountPercent: number;
  subject: string;
  headerText: string;
  footerText: string;
  internalNote: string;
  externalReference: string;
  isFrameworkContract: boolean;
  salesRepId: string;
  deliveryAddressId: string;
  warehouseId: string;
  /** Lieferschein: Rueckmeldung vom Lager (Packstuecke, Gewicht in Gramm, Masse/Bemerkung). */
  packages: number;
  weightGrams: number;
  packNote: string;
};

type DocumentDetail = {
  document: {
    id: string;
    type: DocumentType;
    number: string | null;
    lifecycle: 'draft' | 'posted' | 'cancelled';
    status: string;
    partnerId: string;
    partnerSnapshot: { name: string; city?: string | null } | null;
    contactId: string | null;
    contactSnapshot: { salutation: string | null; firstName: string | null; lastName: string } | null;
    deliveryAddressId: string | null;
    warehouseId: string | null;
    deliveryAddress: {
      name: string;
      street?: string | null;
      postalCode?: string | null;
      city?: string | null;
    } | null;
    documentDate: string;
    serviceDate: string | null;
    dueDate: string | null;
    validUntil: string | null;
    netTotal: number;
    taxTotal: number;
    grossTotal: number;
    paidAmount: number;
    taxSummary: { taxCode: string; label: string; rate: number; netAmount: number; taxAmount: number }[] | null;
    paymentTermId: string | null;
    paymentTermText: string | null;
    discountPercent: number;
    subject: string | null;
    headerText: string | null;
    footerText: string | null;
    internalNote: string | null;
    externalReference: string | null;
    isFrameworkContract: boolean;
    /** Abruf: der Rahmenvertrag, aus dem dieser Auftrag abruft. */
    frameworkContractId: string | null;
    packages: number | null;
    weightGrams: number | null;
    packNote: string | null;
    salesRepId: string | null;
    cancelsDocumentId: string | null;
    cancelledByDocumentId: string | null;
    cancellationReason: string | null;
    postedAt: string | null;
    contentHash: string | null;
  };
  lines: (LineForm & { position: number; netAmount: number })[];
  partner: Partner | null;
  source: { id: string; type: DocumentType; number: string | null } | null;
  successors: { id: string; type: DocumentType; number: string | null; documentDate: string }[];
  followUps: DocumentType[];
  chain: ChainEntry[];
  integrity: { valid: boolean; hash: string | null } | null;
  /** Abruf: Vertrag, aus dem abgerufen wird. */
  frameworkContract: { id: string; number: string | null; validUntil: string | null } | null;
  /** Rahmenvertrag: die Abrufe aus diesem Vertrag. */
  callOffs: CallOffEntry[];
  /** Reservierungsmodus der Installation. */
  reservationMode: 'auto' | 'manual';
};

type CallOffEntry = {
  id: string;
  number: string | null;
  documentDate: string;
  lifecycle: 'draft' | 'posted' | 'cancelled';
  status: string;
  externalReference: string | null;
  netTotal: number;
  cancelledByDocumentId: string | null;
};

/**
 * Zeigt an, ob die Rechnung als E-Rechnung ausgegeben werden kann.
 *
 * Fehlende Pflichtangaben faellt sonst erst beim Empfaenger auf - dann ist
 * die Rechnung bereits raus und muss storniert werden.
 */
function EInvoiceStatus({ documentId }: { documentId: string }) {
  const { data } = useQuery({
    queryKey: ['einvoice-check', documentId],
    queryFn: () =>
      api.get<{ ready: boolean; problems: string[] }>(`/api/documents/${documentId}/einvoice-check`),
  });

  if (!data) return null;

  if (data.ready) {
    return (
      <Alert kind="success">
        Diese Rechnung erfüllt die Anforderungen der E-Rechnung nach EN 16931 und kann als
        XRechnung oder ZUGFeRD ausgegeben werden.
      </Alert>
    );
  }

  return (
    <Alert kind="warning">
      <div>
        <strong>Als E-Rechnung noch nicht versandfähig.</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {data.problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      </div>
    </Alert>
  );
}

let keyCounter = 0;
const nextKey = () => `line-${++keyCounter}`;

function emptyLine(): LineForm {
  return {
    key: nextKey(),
    lineType: 'item',
    itemId: null,
    itemNumber: null,
    description: '',
    descriptionLong: '',
    storageBin: '',
    lots: [],
    tracking: null,
    quantity: 1000,
    unit: 'STK',
    unitPrice: 0,
    discountPercent: 0,
    taxCode: 'USt19',
    sourceLineId: null,
    deliveredQuantity: 0,
    invoicedQuantity: 0,
    calledOffQuantity: 0,
    reservedQuantity: 0,
  };
}

/**
 * Belege mit Lagerwahl: Lieferschein und Wareneingang buchen dort, Auftrag
 * und Bestellung geben das Lager an ihre Folgebelege weiter - ein Kunde mit
 * Rahmenvertrag kann einen eigenen Lagerplatz im Haus haben.
 */
const WAREHOUSE_TYPES = new Set<DocumentType>(['delivery_note', 'goods_receipt', 'sales_order', 'purchase_order']);
/**
 * Lagerplatz je Position nur am Wareneingang (wohin). Im Verkauf entscheidet
 * spaeter die Entnahmeregel des Artikels ueber die Kommissionierung.
 */
const STOCK_TYPES = new Set<DocumentType>(['goods_receipt']);
/**
 * Belegarten, an denen die Chargenfuehrung des Artikels eine Rolle spielt:
 * Wareneingang (Chargen erfassen) und Lieferschein (Chargen entnehmen).
 */
const TRACKING_TYPES = new Set<DocumentType>(['goods_receipt', 'delivery_note']);

export function DocumentDetailPage({ type, framework = false }: { type: DocumentType; framework?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = id === 'neu' || id === undefined;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [mailOpen, setMailOpen] = useState(false);
  const { can } = useAuth();

  const meta = getDocumentType(type);
  // Rahmenvertraege sind Auftraege, leben aber unter eigener Adresse.
  const basePath = framework ? FRAMEWORK_ROUTE : DOCUMENT_ROUTES[type];
  const typeLabel = framework ? 'Rahmenvertrag' : meta.label;
  const side = meta.side;

  const mayWrite = can(side === 'sales' ? 'sales:write' : 'purchase:write');
  /** Rohertrag (Netto minus Einstand) - nur im Verkauf und nur mit Recht. */
  const showMargin = side === 'sales' && can('sales:margin');
  const mayPost = can(side === 'sales' ? 'sales:post' : 'purchase:post');
  const mayCancel = can(side === 'sales' ? 'sales:cancel' : 'purchase:cancel');

  const initialHeader = (): HeaderForm => ({
    partnerId: searchParams.get('partner') ?? '',
    contactId: '',
    documentDate: today(),
    serviceDate: '',
    validUntil: '',
    paymentTermId: '',
    discountPercent: 0,
    subject: '',
    headerText: '',
    footerText: '',
    internalNote: '',
    externalReference: '',
    isFrameworkContract: framework,
    salesRepId: '',
    deliveryAddressId: '',
    warehouseId: '',
    packages: 0,
    weightGrams: 0,
    packNote: '',
  });
  const [header, setHeader] = useState<HeaderForm>(initialHeader);
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState<DocumentDetail | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  /** Meldung des Servers, wenn das Festschreiben am Kreditlimit scheiterte. */
  const [creditConflict, setCreditConflict] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showRelease, setShowRelease] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');

  // --- Ungespeicherte Aenderungen ---------------------------------------------
  // Der gespeicherte Stand wird als Abbild festgehalten (nach dem Laden und
  // nach jedem Speichern); weicht das Formular davon ab, warnt Canalis vor
  // dem Verlassen. Nachgeladene Zusatzinfos (Chargenfuehrung, Vorschlag der
  // Chargenauswahl) zaehlen nicht als Aenderung.
  const savedSnapshot = useRef<string | null>(null);
  const [snapshotPending, setSnapshotPending] = useState(true);
  const snapshotOf = (h: HeaderForm, ls: LineForm[]) =>
    JSON.stringify({
      h,
      ls: ls.map(({ key: _key, tracking: _tracking, costPrice: _costPrice, lotsManual, lots, ...rest }) => ({
        ...rest,
        lots:
          type === 'delivery_note' && !lotsManual
            ? undefined
            : lots.map(({ key: _lotKey, ...lot }) => lot),
      })),
    });
  useEffect(() => {
    if (!snapshotPending) return;
    savedSnapshot.current = snapshotOf(header, lines);
    setSnapshotPending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotPending, header, lines]);

  const { data: partners } = useQuery({
    queryKey: ['partners', side === 'sales' ? 'customer' : 'supplier', 'picker'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(
        withQuery('/api/partners', { role: side === 'sales' ? 'customer' : 'supplier', limit: 500 }),
      ),
  });

  // Anschriften des gewählten Partners für die Lieferanschrift.
  const { data: addresses } = useQuery({
    queryKey: ['partner-addresses', header.partnerId],
    queryFn: () =>
      api.get<{ addresses: PartnerAddress[] }>(`/api/partners/${header.partnerId}/addresses`),
    enabled: Boolean(header.partnerId),
  });

  // Ansprechpartner des gewählten Partners für Anrede und z.-Hd.-Zeile.
  const { data: partnerContactOptions } = useQuery({
    queryKey: ['partner-contacts', header.partnerId],
    queryFn: () =>
      api.get<{
        contacts: {
          id: string;
          salutation: string | null;
          firstName: string | null;
          lastName: string;
          position: string | null;
          isPrimary: boolean;
        }[];
      }>(`/api/partners/${header.partnerId}/contacts`),
    enabled: Boolean(header.partnerId),
  });

  // Lagerorte für Lieferschein und Wareneingang.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () =>
      api.get<{ warehouses: { id: string; code: string; name: string; active: boolean; isDefault?: boolean }[] }>(
        '/api/stock/warehouses',
      ),
    enabled: WAREHOUSE_TYPES.has(type),
  });

  const { data: paymentTerms } = useQuery({
    queryKey: ['payment-terms'],
    queryFn: () => api.get<{ paymentTerms: PaymentTerm[] }>('/api/settings/payment-terms'),
  });

  // Kreditstatus des Kunden - nur fuer die Belegarten, deren Festschreiben
  // der Server gegen das Limit prueft (Auftrag, Lieferschein).
  const creditChecked = type === 'sales_order' || type === 'delivery_note';
  const { data: credit } = useQuery({
    queryKey: ['credit-status', header.partnerId],
    queryFn: () =>
      api.get<{ creditLimit: number; openAmount: number; exceeded: boolean }>(
        `/api/partners/${header.partnerId}/credit-status`,
      ),
    enabled: creditChecked && Boolean(header.partnerId),
  });

  // Neuer normaler Auftrag fuer einen Kunden mit laufendem Rahmenvertrag:
  // Hinweis mit Sprung in den Abruf, damit nicht versehentlich zu
  // Listenpreisen bestellt wird.
  const { data: activeContracts } = useQuery({
    queryKey: ['framework-contracts', header.partnerId],
    queryFn: () =>
      api.get<ListResponse<{ id: string; number: string | null; validUntil: string | null; status: string }>>(
        `/api/documents?type=sales_order&framework=true&lifecycle=posted&partnerId=${encodeURIComponent(header.partnerId)}&limit=20`,
      ),
    enabled: isNew && type === 'sales_order' && !framework && Boolean(header.partnerId),
  });
  const todayIso = new Date().toISOString().slice(0, 10);
  const runningContracts = (activeContracts?.items ?? []).filter(
    (entry) => entry.status !== 'delivered' && (!entry.validUntil || entry.validUntil >= todayIso),
  );

  // Der Beleg wird geladen und erst in einem Effekt ins Formular uebernommen.
  // Wuerde das in der Abfrage selbst passieren, bliebe das Formular beim
  // Wechsel auf einen schon einmal geladenen Beleg (Vorgangskette, Zurueck)
  // auf dem alten Stand: Die Abfrage liefert dann aus dem Zwischenspeicher,
  // ohne noch einmal zu laufen. Dieselbe Seite bleibt dabei bestehen, weil
  // alle Belegarten dieselbe Komponente sind.
  const documentQuery = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.get<DocumentDetail>(`/api/documents/${id}`),
    enabled: !isNew,
  });
  const isLoading = documentQuery.isLoading;

  // Neuer Beleg nach einem bestehenden (oder umgekehrt): Formular leeren.
  useEffect(() => {
    if (!isNew) return;
    setLoaded(null);
    setHeader(initialHeader());
    setLines([emptyLine()]);
    setErrors({});
    setSnapshotPending(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => {
    const result = documentQuery.data;
    if (!result || isNew) return;
    {
      setLoaded(result);
      setHeader({
        partnerId: result.document.partnerId,
        contactId: result.document.contactId ?? '',
        documentDate: result.document.documentDate,
        serviceDate: result.document.serviceDate ?? '',
        validUntil: result.document.validUntil ?? '',
        paymentTermId: result.document.paymentTermId ?? '',
        discountPercent: result.document.discountPercent,
        subject: result.document.subject ?? '',
        headerText: result.document.headerText ?? '',
        footerText: result.document.footerText ?? '',
        internalNote: result.document.internalNote ?? '',
        externalReference: result.document.externalReference ?? '',
        isFrameworkContract: result.document.isFrameworkContract ?? false,
        packages: result.document.packages ?? 0,
        weightGrams: result.document.weightGrams ?? 0,
        packNote: result.document.packNote ?? '',
        salesRepId: result.document.salesRepId ?? '',
        deliveryAddressId: result.document.deliveryAddressId ?? '',
        warehouseId: result.document.warehouseId ?? '',
      });
      const lineLots = (result as { lineLots?: Record<string, { lotId: string | null; lotNumber: string | null; expiryDate: string | null; quantity: number }[]> }).lineLots ?? {};
      const mapped: LineForm[] = result.lines.map((line) => ({
        ...line,
        key: nextKey(),
        descriptionLong: line.descriptionLong ?? '',
        storageBin: (line as { storageBin?: string | null }).storageBin ?? '',
        lots: (lineLots[line.id ?? ''] ?? []).map((lot) => ({
          key: nextKey(),
          lotId: lot.lotId,
          lotNumber: lot.lotNumber ?? '',
          expiryDate: lot.expiryDate ?? '',
          quantity: lot.quantity,
        })),
        // Eine gespeicherte Chargenauswahl am Lieferschein ist eine bewusste
        // Wahl - der Vorschlag darf sie beim Laden nicht ueberschreiben.
        lotsManual: type === 'delivery_note' && (lineLots[line.id ?? ''] ?? []).length > 0,
        tracking: null,
      }));
      setLines(mapped);
      setErrors({});
      setSnapshotPending(true);
      // Chargenfuehrung der Artikel nachladen - sie entscheidet, ob die
      // Chargenerfassung an der Position erscheint.
      // (Bei allen Belegarten - der Einstandspreis fuer den Rohertrag kommt mit.)
      for (const line of mapped) if (line.itemId) void loadItemExtras(line.key, line.itemId, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentQuery.data, isNew]);

  const document = loaded?.document;
  const editable = isNew || document?.lifecycle === 'draft';

  const dirty =
    editable &&
    !snapshotPending &&
    savedSnapshot.current !== null &&
    snapshotOf(header, lines) !== savedSnapshot.current;
  useUnsavedChanges(dirty);

  // Versand per E-Mail: nur fuer festgeschriebene Belege und nur, wenn die
  // Installation ueberhaupt Mails verschickt.
  const mailInfo = useQuery({
    queryKey: ['document-mail', id],
    queryFn: () => api.get<DocumentMailInfo>(`/api/documents/${id}/mail`),
    enabled: !isNew && Boolean(document) && document?.lifecycle !== 'draft' && mayWrite,
  });
  const mayMail = Boolean(mailInfo.data?.available && mailInfo.data.draft);
  const readOnly = !editable || !mayWrite;

  // --- Summen ----------------------------------------------------------------
  // Die Anzeige rechnet mit derselben Logik wie der Server, damit der
  // Anwender die Betraege schon vor dem Speichern sieht.
  const totals = useMemo(() => {
    const itemLines = lines.filter((line) => line.lineType === 'item');
    const withAmounts = itemLines.map((line) => {
      const base = lineAmount(line.quantity, line.unitPrice, line.discountPercent);
      const net =
        header.discountPercent > 0
          ? Math.round(base * (1 - header.discountPercent / 10_000))
          : base;
      return { netAmount: net, taxCode: line.taxCode };
    });
    return calculateTotals(withAmounts);
  }, [lines, header.discountPercent]);

  function lineNet(line: LineForm): number {
    const base = lineAmount(line.quantity, line.unitPrice, line.discountPercent);
    return header.discountPercent > 0
      ? Math.round(base * (1 - header.discountPercent / 10_000))
      : base;
  }

  /** Einstand der Position in Cent: Menge (Tausendstel) x Einstandspreis (Zehntausendstel Euro). */
  function lineCost(line: LineForm): number | null {
    if (line.costPrice === undefined) return null;
    return Math.round((line.quantity * line.costPrice) / 100_000);
  }

  /** Rohertrag ueber alle Positionen mit bekanntem Einstand, fuer den Summenblock. */
  const margin = useMemo(() => {
    if (!showMargin) return null;
    const priced = lines.filter((line) => line.lineType === 'item' && line.costPrice !== undefined);
    if (priced.length === 0) return null;
    const net = priced.reduce((sum, line) => sum + lineNet(line), 0);
    const cost = priced.reduce((sum, line) => sum + (lineCost(line) ?? 0), 0);
    return { profit: net - cost, percent: net > 0 ? ((net - cost) / net) * 100 : null, partial: priced.length < lines.filter((l) => l.lineType === 'item').length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, header.discountPercent, showMargin]);

  // --- Speichern -------------------------------------------------------------
  const payload = () => ({
    type,
    partnerId: header.partnerId,
    contactId: header.contactId || null,
    documentDate: header.documentDate,
    serviceDate: header.serviceDate || null,
    validUntil: header.validUntil || null,
    paymentTermId: header.paymentTermId || null,
    discountPercent: header.discountPercent,
    subject: header.subject || null,
    headerText: header.headerText || null,
    footerText: header.footerText || null,
    internalNote: header.internalNote || null,
    externalReference: header.externalReference || null,
    isFrameworkContract: type === 'sales_order' ? header.isFrameworkContract : undefined,
    ...(type === 'delivery_note'
      ? {
          packages: header.packages > 0 ? header.packages : null,
          weightGrams: header.weightGrams > 0 ? header.weightGrams : null,
          packNote: header.packNote.trim() || null,
        }
      : {}),
    salesRepId: header.salesRepId || null,
    deliveryAddressId: header.deliveryAddressId || null,
    warehouseId: header.warehouseId || null,
    lines: lines.map((line, index) => ({
      position: (index + 1) * 10,
      lineType: line.lineType,
      itemId: line.itemId,
      description: line.description,
      descriptionLong: line.descriptionLong || null,
      storageBin: line.storageBin || null,
      ...(line.lots.length > 0
        ? {
            lots: line.lots.map((lot) => ({
              lotId: lot.lotId,
              lotNumber: lot.lotNumber || null,
              expiryDate: lot.expiryDate || null,
              quantity: lot.quantity,
            })),
          }
        : {}),
      quantity: line.quantity,
      unit: line.unit,
      unitPrice: line.unitPrice,
      discountPercent: line.discountPercent,
      taxCode: line.taxCode,
      sourceLineId: line.sourceLineId,
    })),
  });

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api.post<{ document: { id: string } }>('/api/documents', payload())
        : api.patch<{ document: { id: string } }>(`/api/documents/${id}`, payload()),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      toast.success('Gespeichert.');
      // Der gespeicherte Stand ist jetzt der Bezug fuer die Verlassen-Warnung.
      setSnapshotPending(true);
      if (isNew) navigate(`${basePath}/${result.document.id}`, { replace: true });
    },
    onError: handleError,
  });

  const post = useMutation({
    mutationFn: (ignoreCreditLimit?: boolean) =>
      api.post<{ document: { number: string } }>(`/api/documents/${id}/post`, {
        ignoreCreditLimit: ignoreCreditLimit === true,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setCreditConflict(null);
      toast.success(`${meta.label} ${result.document.number} wurde festgeschrieben.`);
    },
    onError: (error) => {
      // Kreditlimit überschritten: kein Fehler-Toast, sondern die Frage,
      // ob trotzdem festgeschrieben werden soll - die Entscheidung liegt
      // beim Menschen und wird im Protokoll festgehalten.
      if (error instanceof ApiError && error.fieldErrors.reason === 'credit_limit') {
        setCreditConflict(error.message);
        return;
      }
      handleError(error);
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post<{ document: { id: string; number: string } }>(`/api/documents/${id}/cancel`, { reason: cancelReason }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setShowCancel(false);
      toast.success(`Storno ${result.document.number} wurde erstellt.`);
      navigate(`${basePath}/${result.document.id}`);
    },
    onError: handleError,
  });

  // Kommissionieren: Bestand fuer diesen Auftrag reservieren (manueller Modus)
  // bzw. nachreservieren (Automatik).
  const reserve = useMutation({
    mutationFn: () =>
      api.post<{ reservedLines: number; backorderLines: number }>(`/api/documents/${id}/reserve`, {}),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast.success(
        result.backorderLines > 0
          ? `Reserviert. ${result.backorderLines} Position(en) im Rückstand.`
          : 'Der Auftrag ist vollständig reserviert.',
      );
    },
    onError: handleError,
  });

  const releaseReservation = useMutation({
    mutationFn: () =>
      api.post<{ released: true }>(`/api/documents/${id}/release-reservation`, { reason: releaseReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setShowRelease(false);
      setReleaseReason('');
      toast.success('Die Reservierung wurde freigegeben.');
    },
    onError: handleError,
  });

  const followUp = useMutation({
    mutationFn: (targetType: DocumentType) =>
      api.post<{ document: { id: string } }>(`/api/documents/${id}/follow-up`, { targetType }),
    onSuccess: (result, targetType) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(`${getDocumentType(targetType).label} wurde als Entwurf erstellt.`);
      navigate(`${DOCUMENT_ROUTES[targetType]}/${result.document.id}`);
    },
    onError: handleError,
  });

  // Abruf aus einem Rahmenvertrag: neuer Auftrag mit Vertragspreisen und
  // offenen Mengen als Entwurf, Bezug auf den Vertrag.
  const callOffCreate = useMutation({
    mutationFn: (contractId: string) =>
      api.post<{ document: { id: string } }>(`/api/documents/${contractId}/call-off`, {}),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Der Abruf wurde als Auftragsentwurf erstellt.');
      navigate(`${DOCUMENT_ROUTES.sales_order}/${result.document.id}`);
    },
    onError: handleError,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Der Entwurf wurde gelöscht.');
      navigate(basePath);
    },
    onError: handleError,
  });

  function handleError(error: unknown) {
    if (error instanceof ApiError) {
      setErrors(error.fieldErrors);
      toast.error(error.message);
    } else {
      toast.error('Die Aktion ist fehlgeschlagen.');
    }
  }

  if (!isNew && isLoading) return <LoadingBlock />;

  // --- Zeilenbearbeitung -----------------------------------------------------
  // Artikelstamm fuer die Position nachladen: Lagerplatz fuer das Lager des
  // Belegs (nur als Vorschlag bei neuer Wahl) und die Chargenfuehrung.
  async function loadItemExtras(key: string, itemId: string, suggestBin: boolean) {
    try {
      const result = await api.get<{
        item: { trackingMode?: string; hasExpiry?: boolean; pickingStrategy?: string };
        /** Einstand fuer den Rohertrag (Set: Summe der Bestandteile; sonst gleitend, ohne Zugang der Einkaufspreis). */
        costPrice?: number;
        storageBins: { warehouseId: string; bin: string }[];
      }>(`/api/items/${itemId}`);
      const changes: Partial<LineForm> = {
        tracking: {
          mode: (result.item.trackingMode as 'none' | 'batch' | 'serial' | undefined) ?? 'none',
          hasExpiry: result.item.hasExpiry ?? false,
          strategy: (result.item.pickingStrategy as 'fifo' | 'fefo' | 'manual' | undefined) ?? 'fifo',
        },
      };
      // Einstandspreis fuer den Rohertrag - bei neuen Positionen der aktuelle
      // Stand aus dem Artikel; gespeicherte Positionen behalten ihren Wert.
      if (showMargin && typeof result.costPrice === 'number') {
        const costPrice = result.costPrice;
        setLines((current) =>
          current.map((line) => (line.key === key && line.costPrice === undefined ? { ...line, costPrice } : line)),
        );
      }
      if (suggestBin) {
        const warehouseId = header.warehouseId || warehouses?.warehouses.find((w) => w.isDefault)?.id;
        const bin = result.storageBins.find((entry) => entry.warehouseId === warehouseId)?.bin ?? '';
        if (bin) changes.storageBin = bin;
      }
      updateLine(key, changes);
    } catch {
      /* nur Zusatzinformation - ohne sie geht es weiter */
    }
  }

  function updateLine(key: string, changes: Partial<LineForm>) {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...changes } : line)),
    );
  }

  function addLine(lineType: LineForm['lineType'] = 'item') {
    setLines((current) => [...current, { ...emptyLine(), lineType }]);
  }

  function removeLine(key: string) {
    setLines((current) => (current.length === 1 ? [emptyLine()] : current.filter((line) => line.key !== key)));
  }

  function moveLine(key: string, direction: -1 | 1) {
    setLines((current) => {
      const index = current.findIndex((line) => line.key === key);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;

      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
  }

  const title = isNew
    ? `${typeLabel} erfassen`
    : `${document?.isFrameworkContract ? 'Rahmenvertrag' : meta.label} ${document?.number ?? '(Entwurf)'}`;
  // Abruf-Fortschritt eines festgeschriebenen Rahmenvertrags, wertgewichtet.
  const callOff =
    document?.isFrameworkContract && document.lifecycle === 'posted'
      ? callOffPercent({
          orderedValue: lines.reduce((sum, line) => sum + (line.itemId ? line.quantity * line.unitPrice : 0), 0),
          calledOffValue: lines.reduce((sum, line) => sum + (line.itemId ? line.calledOffQuantity * line.unitPrice : 0), 0),
        })
      : null;

  return (
    <>
      {mailOpen && document && id && mailInfo.data ? (
        <DocumentMailDialog
          documentId={id}
          label={`${meta.label} ${document.number ?? ''}`}
          info={mailInfo.data}
          onClose={() => setMailOpen(false)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['document', id] });
            queryClient.invalidateQueries({ queryKey: ['document-mail', id] });
            queryClient.invalidateQueries({ queryKey: ['attachments', id] });
          }}
        />
      ) : null}
      <PageHeader
        title={title}
        subtitle={
          document
            ? `${DOCUMENT_LIFECYCLE_LABELS[document.lifecycle]}${document.postedAt ? ` am ${formatDate(document.postedAt.slice(0, 10))}` : ''}${
                callOff !== null ? ` · abgerufen ${callOff} %` : ''
              }`
            : undefined
        }
        actions={
          <>
            <button type="button" className="btn" onClick={() => confirmLeave(dirty) && navigate(basePath)}>
              Zurück
            </button>

            {!isNew ? (
              <a
                className="btn"
                href={apiUrl(`/api/documents/${id}/pdf`)}
                target="_blank"
                rel="noreferrer"
                title={
                  document?.lifecycle === 'draft'
                    ? 'Zeigt den zuletzt gespeicherten Stand des Entwurfs'
                    : undefined
                }
              >
                {document?.lifecycle === 'draft' ? 'PDF-Vorschau' : 'PDF öffnen'}
              </a>
            ) : null}

            {!isNew && type === 'delivery_note' ? (
              <a
                className="btn"
                href={apiUrl(`/api/documents/${id}/packliste`)}
                target="_blank"
                rel="noreferrer"
                title="Blatt fürs Lager: Positionen nach Lagerplatz, Chargen, Kästchen zum Abhaken, Rückmeldefelder"
              >
                Packliste
              </a>
            ) : null}

            {mayMail ? (
              <button type="button" className="btn" onClick={() => setMailOpen(true)}>
                Per E-Mail senden
              </button>
            ) : null}

            {!isNew &&
            document?.lifecycle !== 'draft' &&
            (type === 'invoice' || type === 'credit_note') ? (
              <a className="btn" href={apiUrl(`/api/documents/${id}/xrechnung`)}>
                XRechnung
              </a>
            ) : null}

            {!isNew && editable && mayWrite ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                Entwurf löschen
              </button>
            ) : null}

            {editable && mayWrite ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setErrors({});
                  save.mutate();
                }}
                disabled={save.isPending}
              >
                {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
              </button>
            ) : null}

            {!isNew && editable && mayPost ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => post.mutate(false)}
                disabled={post.isPending}
              >
                Festschreiben
              </button>
            ) : null}

            {document?.lifecycle === 'posted' && !document.cancelledByDocumentId && mayCancel ? (
              <button type="button" className="btn btn-danger" onClick={() => setShowCancel(true)}>
                Stornieren
              </button>
            ) : null}
          </>
        }
      />

      {/* --- Hinweise ---------------------------------------------------- */}
      {document?.lifecycle === 'posted' ? (
        <Alert kind="info">
          Dieser Beleg ist festgeschrieben und kann nicht mehr geändert werden. Für eine Korrektur
          stornieren Sie ihn und erfassen ihn neu.
        </Alert>
      ) : null}

      {document?.lifecycle === 'cancelled' ? (
        <Alert kind="error">
          <div>
            <strong>Dieser Beleg wurde storniert.</strong>
            {document.cancellationReason ? <div>Grund: {document.cancellationReason}</div> : null}
          </div>
        </Alert>
      ) : null}

      {loaded?.integrity && !loaded.integrity.valid ? (
        <Alert kind="error">
          <strong>Die Prüfsumme dieses Belegs stimmt nicht.</strong> Der Inhalt wurde nach dem
          Festschreiben verändert — vermutlich direkt in der Datenbank. Bitte prüfen Sie das
          Änderungsprotokoll.
        </Alert>
      ) : null}

      {errors._ ? <Alert kind="error">{errors._}</Alert> : null}

      {/* --- Satzsperre ---------------------------------------------------- */}
      {loaded?.partner?.lockedAt ? (
        <Alert kind="error">
          <strong>
            {loaded.partner.number} {loaded.partner.name} ist gesperrt
          </strong>{' '}
          (seit {new Date(loaded.partner.lockedAt).toLocaleDateString('de-DE')}
          {loaded.partner.lockedByName ? ` von ${loaded.partner.lockedByName}` : ''})
          {loaded.partner.lockReason ? `: ${loaded.partner.lockReason}` : ''}. Solange die Sperre besteht, lässt
          sich dieser Beleg nicht festschreiben und es können keine neuen Belege angelegt werden.
        </Alert>
      ) : null}

      {/* --- Laufender Rahmenvertrag beim neuen Auftrag -------------------- */}
      {isNew && runningContracts.length > 0 ? (
        <Alert kind="info">
          <div>
            <strong>
              Für diesen Kunden läuft {runningContracts.length === 1 ? 'ein Rahmenvertrag' : `${runningContracts.length} Rahmenverträge`}.
            </strong>{' '}
            Ein Abruf übernimmt Vertragspreise und offene Mengen und zählt am Vertrag. Dieser Auftrag hier
            wäre ein normaler Auftrag zu eigenen Preisen.
            <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {runningContracts.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="btn btn-sm"
                  disabled={callOffCreate.isPending}
                  onClick={() => callOffCreate.mutate(entry.id)}
                >
                  Abruf aus Rahmenvertrag {entry.number ?? ''}
                  {entry.validUntil ? ` (bis ${formatDate(entry.validUntil)})` : ''}
                </button>
              ))}
            </div>
          </div>
        </Alert>
      ) : null}

      {/* --- Kreditlimit --------------------------------------------------- */}
      {creditConflict ? (
        <Alert kind="warning">
          <div>
            <strong>{creditConflict}</strong>
            <div className="row" style={{ gap: 6, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={post.isPending}
                onClick={() => post.mutate(true)}
              >
                Trotzdem festschreiben
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setCreditConflict(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </Alert>
      ) : null}

      {creditChecked && credit && credit.creditLimit > 0 ? (
        <div className="card">
          <div className="card-body">
            <CreditGauge
              creditLimit={credit.creditLimit}
              openAmount={credit.openAmount}
              pending={editable ? totals.grossTotal : 0}
            />
          </div>
        </div>
      ) : null}

      {!creditConflict &&
      editable &&
      creditChecked &&
      credit &&
      credit.creditLimit > 0 &&
      credit.openAmount + totals.grossTotal > credit.creditLimit ? (
        <Alert kind="warning">
          Mit diesem Beleg würde das Kreditlimit des Kunden überschritten:{' '}
          {formatAmount(credit.openAmount)} € offene Posten plus {formatAmount(totals.grossTotal)} €
          Belegwert übersteigen das Limit von {formatAmount(credit.creditLimit)} €. Das
          Festschreiben fragt dann nach einer Bestätigung.
        </Alert>
      ) : null}

      {/* --- E-Rechnung ---------------------------------------------------- */}
      {(type === 'invoice' || type === 'credit_note') && document?.lifecycle === 'posted' ? (
        <EInvoiceStatus documentId={document.id} />
      ) : null}

      {/* --- Vorgangsfolge ------------------------------------------------- */}
      {loaded && loaded.chain.length > 1 ? (
        <div className="card">
          <div className="card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <span className="muted">
                Dieser Beleg gehört zu einem Vorgang aus {loaded.chain.length} Belegen.
              </span>
              <span className="spacer" style={{ flex: 1 }} />
              <Link className="btn btn-sm" to={`/vorgang/${id}`}>
                Vorgangsfolge ansehen
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {/* --- Rahmenvertrag: Abrufe ---------------------------------------- */}
      {loaded && document?.isFrameworkContract && document.lifecycle === 'posted' ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h2>Abrufe</h2>
              <div className="faint" style={{ fontSize: 12.5 }}>
                Eigene Aufträge mit Bezug auf den Vertrag — Vertragspreise, Bestellnummer und
                Lieferanschrift je Abruf
              </div>
            </div>
            {mayWrite && !document.cancelledByDocumentId ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={callOffCreate.isPending}
                onClick={() => callOffCreate.mutate(document.id)}
              >
                Abruf anlegen
              </button>
            ) : null}
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Position</th>
                  <th className="numeric">Bestellt</th>
                  <th className="numeric">Abgerufen</th>
                  <th className="numeric">Geliefert</th>
                  <th className="numeric">Offen</th>
                </tr>
              </thead>
              <tbody>
                {lines
                  .filter((line) => line.lineType === 'item')
                  .map((line) => (
                    <tr key={line.key}>
                      <td>{line.description}</td>
                      <td className="numeric">{formatQuantity(line.quantity)} {line.unit}</td>
                      <td className="numeric">{formatQuantity(line.calledOffQuantity)}</td>
                      <td className="numeric">{formatQuantity(line.deliveredQuantity)}</td>
                      <td className="numeric" style={{ fontWeight: 500 }}>
                        {formatQuantity(Math.max(0, line.quantity - line.calledOffQuantity))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {loaded.callOffs.length > 0 ? (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Datum</th>
                    <th>Abruf</th>
                    <th>Bestellnummer des Kunden</th>
                    <th className="shrink">Stand</th>
                    <th className="numeric">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.callOffs.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.documentDate)}</td>
                      <td>
                        <Link to={`${DOCUMENT_ROUTES.sales_order}/${entry.id}`}>
                          Auftrag {entry.number ?? '(Entwurf)'}
                        </Link>
                      </td>
                      <td>{entry.externalReference ?? ''}</td>
                      <td>
                        {entry.cancelledByDocumentId ? 'storniert' : DOCUMENT_LIFECYCLE_LABELS[entry.lifecycle]}
                      </td>
                      <td className="numeric">{formatAmount(entry.netTotal)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card-body faint" style={{ paddingTop: 10, paddingBottom: 12 }}>
              Noch keine Abrufe. Lieferscheine direkt aus dem Vertrag zählen ebenfalls als abgerufen.
            </div>
          )}
        </div>
      ) : null}

      {/* --- Reservierung / Kommissionierung ------------------------------ */}
      {loaded &&
      type === 'sales_order' &&
      !document?.isFrameworkContract &&
      document?.lifecycle === 'posted' &&
      !document.cancelledByDocumentId ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h2>Reservierung</h2>
              <div className="faint" style={{ fontSize: 12.5 }}>
                {loaded.reservationMode === 'auto'
                  ? 'Automatisch beim Festschreiben reserviert. „Nachreservieren" holt frei gewordenen Bestand.'
                  : 'Bestand wird per „Kommissionieren" für diesen Auftrag gebunden.'}
              </div>
            </div>
            {mayWrite ? (
              <div className="row" style={{ gap: 6 }}>
                {lines.some((line) => line.reservedQuantity > 0) ? (
                  <button type="button" className="btn btn-sm" onClick={() => setShowRelease(true)}>
                    Freigeben
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={reserve.isPending}
                  onClick={() => reserve.mutate()}
                >
                  {loaded.reservationMode === 'auto' ? 'Nachreservieren' : 'Kommissionieren'}
                </button>
              </div>
            ) : null}
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Position</th>
                  <th className="numeric">Bestellt</th>
                  <th className="numeric">Reserviert</th>
                  <th className="numeric">Geliefert</th>
                  <th className="numeric">Rückstand</th>
                </tr>
              </thead>
              <tbody>
                {lines
                  .filter((line) => line.lineType === 'item')
                  .map((line) => {
                    const rueckstand = Math.max(
                      0,
                      line.quantity - line.deliveredQuantity - line.reservedQuantity,
                    );
                    return (
                      <tr key={line.key}>
                        <td>{line.description}</td>
                        <td className="numeric">{formatQuantity(line.quantity)} {line.unit}</td>
                        <td className="numeric">{formatQuantity(line.reservedQuantity)}</td>
                        <td className="numeric">{formatQuantity(line.deliveredQuantity)}</td>
                        <td className="numeric" style={rueckstand > 0 ? { fontWeight: 500, color: 'var(--danger)' } : undefined}>
                          {formatQuantity(rueckstand)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* --- Folgebelege -------------------------------------------------- */}
      {loaded && loaded.followUps.length > 0 && mayWrite ? (
        <div className="card">
          <div className="card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <div className="row" style={{ gap: 10 }}>
              <span className="muted">Weiterführen als:</span>
              {loaded.followUps.map((target) => (
                <button
                  key={target}
                  type="button"
                  className="btn btn-sm"
                  disabled={followUp.isPending}
                  onClick={() => followUp.mutate(target)}
                >
                  {getDocumentType(target).label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* --- Kopfdaten ---------------------------------------------------- */}
      <div className="card">
        <div className="card-body">
          <div className="form-grid">
            <Field
              label={side === 'sales' ? 'Kunde' : 'Lieferant'}
              span={5}
              required
              error={errors.partnerId}
            >
              {readOnly ? (
                <input autoComplete="off"
                  className="input"
                  disabled
                  value={
                    document?.partnerSnapshot?.name ??
                    loaded?.partner?.name ??
                    ''
                  }
                />
              ) : (
                <SearchSelect
                  value={header.partnerId}
                  onChange={(value) => {
                    // Der Aussendienst des Kunden belegt das Feld vor,
                    // solange niemand bewusst etwas anderes gewaehlt hat.
                    const partner = partners?.items.find((entry) => entry.id === value);
                    // Kundenrabatt aus der Kundenakte vorbelegen - nur im Verkauf
                    // und nur, solange am Beleg noch kein Rabatt eingetragen ist.
                    const previousDefault = partners?.items.find((entry) => entry.id === header.partnerId)
                      ?.defaultDiscountPercent ?? 0;
                    const discountUntouched = header.discountPercent === 0 || header.discountPercent === previousDefault;
                    const discountPercent =
                      side === 'sales' && discountUntouched
                        ? (partner?.defaultDiscountPercent ?? 0)
                        : header.discountPercent;
                    setHeader({
                      ...header,
                      partnerId: value,
                      discountPercent,
                      // Der Ansprechpartner gehoert zum alten Partner.
                      contactId: '',
                      salesRepId: partner?.salesRepId ?? header.salesRepId,
                    });
                    // Vorgabe-Ansprechpartner des neuen Partners vorbelegen.
                    api
                      .get<{ contacts: { id: string; isPrimary: boolean }[] }>(
                        `/api/partners/${value}/contacts`,
                      )
                      .then((result) => {
                        const primary = result.contacts.find((entry) => entry.isPrimary);
                        if (!primary) return;
                        setHeader((current) =>
                          current.partnerId === value && !current.contactId
                            ? { ...current, contactId: primary.id }
                            : current,
                        );
                      })
                      .catch(() => undefined);
                  }}
                  placeholder="Name oder Nummer tippen …"
                  options={(partners?.items ?? []).map((partner) => ({
                    value: partner.id,
                    label: `${partner.number} · ${partner.name}${partner.city ? `, ${partner.city}` : ''}`,
                  }))}
                  invalid={Boolean(errors.partnerId)}
                />
              )}
              {header.partnerId ? (
                // Neuer Tab, damit der Beleg samt Eingaben stehen bleibt.
                <Link
                  to={`${side === 'sales' ? '/kunden' : '/lieferanten'}/${header.partnerId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="faint"
                  style={{ fontSize: 12, display: 'inline-block', marginTop: 4 }}
                >
                  Akte in neuem Tab öffnen ↗
                </Link>
              ) : null}
            </Field>

            <Field
              label="Ansprechpartner"
              span={3}
              error={errors.contactId}
              hint="Erscheint in Anrede und z.-Hd.-Zeile"
            >
              {readOnly ? (
                <input autoComplete="off"
                  className="input"
                  disabled
                  value={
                    document?.contactSnapshot ? contactDisplayName(document.contactSnapshot) : ''
                  }
                />
              ) : (
                <Select
                  value={header.contactId}
                  onChange={(value) => setHeader({ ...header, contactId: value })}
                  placeholder="— ohne Ansprechpartner —"
                  options={(partnerContactOptions?.contacts ?? []).map((contact) => ({
                    value: contact.id,
                    label:
                      contactDisplayName(contact) +
                      (contact.position ? ` (${contact.position})` : ''),
                  }))}
                  disabled={!header.partnerId}
                />
              )}
            </Field>

            <Field label="Belegdatum" span={2} required error={errors.documentDate}>
              <TextInput
                type="date"
                value={header.documentDate}
                onChange={(value) => setHeader({ ...header, documentDate: value })}
                disabled={readOnly}
                invalid={Boolean(errors.documentDate)}
              />
            </Field>

            {type === 'invoice' || type === 'credit_note' || type === 'delivery_note' ? (
              <Field
                label={type === 'delivery_note' ? 'Lieferdatum' : 'Leistungsdatum'}
                span={2}
                error={errors.serviceDate}
                hint={type === 'invoice' ? 'Pflicht nach § 14 UStG' : undefined}
              >
                <TextInput
                  type="date"
                  value={header.serviceDate}
                  onChange={(value) => setHeader({ ...header, serviceDate: value })}
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            {type === 'quote' || header.isFrameworkContract ? (
              <Field label="Gültig bis" span={2} error={errors.validUntil} hint={header.isFrameworkContract ? 'Laufzeit des Vertrags' : undefined}>
                <TextInput
                  type="date"
                  value={header.validUntil}
                  onChange={(value) => setHeader({ ...header, validUntil: value })}
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            <Field label="Ihre Bestellnummer" span={3} error={errors.externalReference}>
              <TextInput
                value={header.externalReference}
                onChange={(value) => setHeader({ ...header, externalReference: value })}
                disabled={readOnly}
              />
            </Field>

            {type === 'sales_order' && loaded?.frameworkContract ? (
              <Field label="Abruf aus" span={2} hint="Vertragspreise, Mengen zählen am Vertrag">
                <div style={{ paddingTop: 6 }}>
                  <Link to={`${FRAMEWORK_ROUTE}/${loaded.frameworkContract.id}`}>
                    Rahmenvertrag {loaded.frameworkContract.number ?? ''}
                  </Link>
                  {loaded.frameworkContract.validUntil ? (
                    <div className="faint" style={{ fontSize: 12 }}>
                      gültig bis {formatDate(loaded.frameworkContract.validUntil)}
                    </div>
                  ) : null}
                </div>
              </Field>
            ) : null}
            {/* Kein Haekchen "Rahmenvertrag" mehr: Ob ein Auftrag ein Rahmenvertrag ist,
                entscheidet der Menuepunkt, ueber den er angelegt wurde (Nutzerentscheidung
                04./10.09.2026). Der Titel der Seite zeigt es an. */}

            <Field
              label="Betreff"
              span={type === 'sales_order' && loaded?.frameworkContract ? 5 : 7}
              error={errors.subject}
            >
              <TextInput
                value={header.subject}
                onChange={(value) => setHeader({ ...header, subject: value })}
                disabled={readOnly}
              />
            </Field>

            {side === 'sales' ? (
              <Field
                label="Zuständiger Außendienst"
                span={5}
                error={errors.salesRepId}
                hint="Vorbelegt vom Kunden; der Erfasser wird davon unabhängig festgehalten"
              >
                <UserSelect
                  value={header.salesRepId}
                  onChange={(value) => setHeader({ ...header, salesRepId: value })}
                  emptyLabel="Ohne Außendienst"
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            {type === 'invoice' || type === 'purchase_invoice' ? (
              <Field label="Zahlungsbedingung" span={5} error={errors.paymentTermId}>
                <Select
                  value={header.paymentTermId}
                  onChange={(value) => setHeader({ ...header, paymentTermId: value })}
                  placeholder="— Vorgabe —"
                  options={(paymentTerms?.paymentTerms ?? []).map((term) => ({
                    value: term.id,
                    label: term.name,
                  }))}
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            {/* Lagerort: Bestand bewegende Belege buchen dort, Auftrag und Bestellung geben ihn weiter. */}
            {WAREHOUSE_TYPES.has(type) ? (
              <Field
                label={
                  type === 'delivery_note'
                    ? 'Ware entnehmen aus'
                    : type === 'goods_receipt'
                      ? 'Ware einlagern in'
                      : 'Lager'
                }
                span={5}
                error={errors.warehouseId}
                hint={
                  type === 'delivery_note' || type === 'goods_receipt'
                    ? 'Bestimmt, welcher Lagerort beim Festschreiben gebucht wird'
                    : 'Vorgabe für die Folgebelege, z. B. der eigene Lagerplatz des Kunden'
                }
              >
                <Select
                  value={header.warehouseId}
                  onChange={(value) => setHeader({ ...header, warehouseId: value })}
                  placeholder="— Vorgabelager —"
                  options={(warehouses?.warehouses ?? [])
                    .filter((warehouse) => warehouse.active)
                    .map((warehouse) => ({
                      value: warehouse.id,
                      label: `${warehouse.code} · ${warehouse.name}`,
                    }))}
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            {/* Lieferanschrift: nur anbieten, wenn der Partner welche hat. */}
            {addresses && addresses.addresses.length > 0 ? (
              <Field
                label="Lieferanschrift"
                span={6}
                error={errors.deliveryAddressId}
                hint="Wird beim Speichern in den Beleg übernommen"
              >
                <Select
                  value={header.deliveryAddressId}
                  onChange={(value) => setHeader({ ...header, deliveryAddressId: value })}
                  placeholder="— Hauptanschrift des Partners —"
                  options={addresses.addresses
                    .filter((address) => address.addressType === 'delivery')
                    .map((address) => ({
                      value: address.id,
                      label: `${address.label ? `${address.label} · ` : ''}${address.name}, ${address.postalCode ?? ''} ${address.city ?? ''}`.trim(),
                    }))}
                  disabled={readOnly}
                />
              </Field>
            ) : null}

            {/* Bei festgeschriebenen Belegen zählt die Kopie im Beleg. */}
            {readOnly && document?.deliveryAddress ? (
              <Field label="Lieferanschrift laut Beleg" span={6}>
                <div
                  style={{
                    padding: '6px 9px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-sunken)',
                    fontSize: 13.5,
                  }}
                >
                  {document.deliveryAddress.name}
                  {document.deliveryAddress.street ? `, ${document.deliveryAddress.street}` : ''}
                  {document.deliveryAddress.city
                    ? `, ${document.deliveryAddress.postalCode ?? ''} ${document.deliveryAddress.city}`
                    : ''}
                </div>
              </Field>
            ) : null}

            <Field label="Einleitungstext" span={12} error={errors.headerText}>
              <TextArea
                value={header.headerText}
                onChange={(value) => setHeader({ ...header, headerText: value })}
                rows={2}
                disabled={readOnly}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* --- Positionen --------------------------------------------------- */}
      <div className="card">
        <div className="card-header">
          <h2>Positionen</h2>
          {!readOnly ? (
            <div className="row" style={{ gap: 6 }}>
              <button type="button" className="btn btn-sm" onClick={() => addLine('item')}>
                + Artikel
              </button>
              <button type="button" className="btn btn-sm" onClick={() => addLine('text')}>
                + Textzeile
              </button>
            </div>
          ) : null}
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="shrink">Pos</th>
                <th style={{ minWidth: 260 }}>Bezeichnung</th>
                <th className="numeric" style={{ width: 90 }}>Menge</th>
                <th style={{ width: 90 }}>Einheit</th>
                <th className="numeric" style={{ width: 110 }}>Einzelpreis</th>
                <th className="numeric" style={{ width: 80 }}>Rabatt %</th>
                {showMargin ? (
                  <th className="numeric" style={{ width: 120 }} title="Rohertrag: Netto minus Einstandspreis">
                    Rohertrag
                  </th>
                ) : null}
                <th style={{ width: 120 }}>Steuer</th>
                <th className="numeric" style={{ width: 110 }}>Betrag</th>
                {!readOnly ? <th className="shrink"></th> : null}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <Fragment key={line.key}>
                <tr>
                  <td className="faint">{line.lineType === 'item' ? index + 1 : ''}</td>

                  <td>
                    {line.lineType === 'text' ? (
                      <TextInput
                        value={line.description}
                        onChange={(value) => updateLine(line.key, { description: value })}
                        disabled={readOnly}
                        placeholder="Zwischentext"
                      />
                    ) : readOnly ? (
                      <>
                        <div>{line.description}</div>
                        {line.itemNumber ? (
                          <div className="faint mono" style={{ fontSize: 12 }}>
                            {line.itemNumber}
                          </div>
                        ) : null}
                        {line.storageBin ? (
                          <div className="faint" style={{ fontSize: 12 }}>
                            Lagerplatz <span className="mono">{line.storageBin}</span>
                          </div>
                        ) : null}
                        {line.descriptionLong ? (
                          <div className="faint" style={{ fontSize: 12 }}>
                            {line.descriptionLong}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="stack" style={{ gap: 4 }}>
                        <ItemPicker
                          value={line.description}
                          onSelect={(item) => {
                            updateLine(line.key, {
                              itemId: item.id,
                              itemNumber: item.number,
                              description: item.name,
                              descriptionLong: item.description ?? '',
                              unit: item.unit,
                              taxCode: item.taxCode,
                              unitPrice: side === 'sales' ? item.salesPrice : item.purchasePrice,
                            });
                            // Chargenfuehrung und Einstandspreis nachladen; den Lagerplatz nur am WE vorschlagen.
                            void loadItemExtras(line.key, item.id, STOCK_TYPES.has(type));
                          }}
                        />
                        {line.itemNumber ? (
                          <span className="faint mono" style={{ fontSize: 12 }}>
                            {line.itemNumber}
                          </span>
                        ) : null}
                        {STOCK_TYPES.has(type) && line.itemId ? (
                          <TextInput
                            value={line.storageBin}
                            onChange={(value) => updateLine(line.key, { storageBin: value })}
                            placeholder="Lagerplatz (wohin), z. B. M-A-221"
                          />
                        ) : null}
                        {line.sourceLineId ? (
                          <span className="faint" style={{ fontSize: 11.5 }}>
                            aus Vorbeleg übernommen
                          </span>
                        ) : null}
                      </div>
                    )}
                  </td>

                  {line.lineType === 'item' ? (
                    <>
                      <td>
                        <NumericInput
                          kind="quantity"
                          value={line.quantity}
                          onChange={(value) => updateLine(line.key, { quantity: value })}
                          disabled={readOnly}
                          allowNegative
                        />
                      </td>
                      <td>
                        <Select
                          value={line.unit}
                          onChange={(value) => updateLine(line.key, { unit: value })}
                          options={unitList().map((unit) => ({
                            value: unit.code,
                            label: unit.short,
                          }))}
                          disabled={readOnly}
                        />
                      </td>
                      <td>
                        <NumericInput
                          kind="price"
                          value={line.unitPrice}
                          onChange={(value) => updateLine(line.key, { unitPrice: value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td>
                        <NumericInput
                          kind="amount"
                          value={line.discountPercent}
                          onChange={(value) =>
                            updateLine(line.key, { discountPercent: Math.min(10_000, value) })
                          }
                          disabled={readOnly}
                        />
                      </td>
                      {showMargin ? (
                        <td className="numeric" style={{ paddingTop: 12, whiteSpace: 'nowrap' }}>
                          {lineCost(line) !== null ? (
                            <span title={`Einstand ${formatAmount(lineCost(line)!)} €`}>
                              {formatAmount(lineNet(line) - lineCost(line)!)} €
                              <span className="faint" style={{ fontSize: 11.5 }}>
                                {lineNet(line) > 0 ? ` · ${(((lineNet(line) - lineCost(line)!) / lineNet(line)) * 100).toFixed(1)} %` : ''}
                              </span>
                            </span>
                          ) : (
                            <span className="faint">–</span>
                          )}
                        </td>
                      ) : null}
                      <td>
                        <Select
                          value={line.taxCode}
                          onChange={(value) => updateLine(line.key, { taxCode: value })}
                          options={Object.values(TAX_CODES).map((code) => ({
                            value: code.code,
                            label: `${code.rate / 100} %`,
                          }))}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="numeric" style={{ fontWeight: 500, paddingTop: 12 }}>
                        {formatAmount(lineNet(line))}
                      </td>
                    </>
                  ) : (
                    <td colSpan={showMargin ? 7 : 6} />
                  )}

                  {!readOnly ? (
                    <td>
                      <div className="row" style={{ gap: 2, paddingTop: 4 }}>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          title="Nach oben"
                          onClick={() => moveLine(line.key, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          title="Nach unten"
                          onClick={() => moveLine(line.key, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          title="Position entfernen"
                          onClick={() => removeLine(line.key)}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
                {(type === 'goods_receipt' || type === 'delivery_note') &&
                line.lineType === 'item' &&
                line.itemId &&
                line.tracking &&
                (line.tracking.mode !== 'none' || line.tracking.hasExpiry) ? (
                  <tr className="line-lots">
                    <td></td>
                    <td colSpan={(readOnly ? 7 : 8) + (showMargin ? 1 : 0)}>
                      {type === 'goods_receipt' ? (
                        <LotEditor
                          line={line}
                          readOnly={readOnly}
                          onChange={(lots) => updateLine(line.key, { lots })}
                        />
                      ) : (
                        <LotPicker
                          line={line}
                          warehouseId={header.warehouseId || warehouses?.warehouses.find((w) => w.isDefault)?.id || ''}
                          readOnly={readOnly}
                          onChange={(lots, manual) => updateLine(line.key, { lots, lotsManual: manual })}
                        />
                      )}
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* --- Summen ---------------------------------------------------- */}
        <div className="card-footer" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 340, width: '100%' }}>
            {!readOnly ? (
              <Field
                label="Rabatt auf alle Positionen in %"
                span={12}
                hint={side === 'sales' ? 'Vorbelegt mit dem Kundenrabatt aus der Kundenakte, hier änderbar' : undefined}
              >
                <NumericInput
                  kind="amount"
                  value={header.discountPercent}
                  onChange={(value) =>
                    setHeader({ ...header, discountPercent: Math.min(10_000, value) })
                  }
                />
              </Field>
            ) : null}
          </div>

          <div style={{ minWidth: 280 }}>
            <div className="row-between" style={{ padding: '3px 0' }}>
              <span className="muted">Summe netto</span>
              <span className="mono">{formatAmount(totals.netTotal)} €</span>
            </div>
            {margin ? (
              <div
                className="row-between faint"
                style={{ padding: '3px 0', fontSize: 12.5 }}
                title={margin.partial ? 'Positionen ohne bekannten Einstandspreis sind nicht enthalten' : 'Netto minus Einstandspreis der Positionen'}
              >
                <span>Rohertrag{margin.partial ? ' (teilweise)' : ''}</span>
                <span className="mono">
                  {formatAmount(margin.profit)} €{margin.percent !== null ? ` · ${margin.percent.toFixed(1)} %` : ''}
                </span>
              </div>
            ) : null}
            {totals.taxSummary.map((entry) => (
              <div className="row-between" key={entry.taxCode} style={{ padding: '3px 0' }}>
                <span className="muted">
                  {entry.rate > 0
                    ? `zzgl. ${formatAmount(entry.rate)} % USt`
                    : entry.label}
                </span>
                <span className="mono">{formatAmount(entry.taxAmount)} €</span>
              </div>
            ))}
            <div
              className="row-between"
              style={{
                padding: '7px 0 0',
                marginTop: 5,
                borderTop: '1px solid var(--border-strong)',
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              <span>Gesamtbetrag</span>
              <span className="mono">{formatAmount(totals.grossTotal)} €</span>
            </div>
            {document && document.lifecycle === 'posted' && document.paidAmount > 0 ? (
              <div className="row-between" style={{ padding: '3px 0' }}>
                <span className="muted">Davon bezahlt</span>
                <span className="mono">{formatAmount(document.paidAmount)} €</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* --- Schlusstext und Notiz ---------------------------------------- */}
      <div className="card">
        <div className="card-body">
          <div className="form-grid">
            <Field label="Schlusstext" span={6}>
              <TextArea
                value={header.footerText}
                onChange={(value) => setHeader({ ...header, footerText: value })}
                rows={3}
                disabled={readOnly}
              />
            </Field>
            <Field label="Interne Notiz" span={6} hint="Erscheint nicht auf dem Beleg">
              <TextArea
                value={header.internalNote}
                onChange={(value) => setHeader({ ...header, internalNote: value })}
                rows={3}
                disabled={readOnly}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* --- Ausgangsjournal ----------------------------------------------- */}
      {!isNew && document && document.lifecycle !== 'draft' ? (
        <DispatchJournalCard documentId={id!} mayWrite={mayWrite} />
      ) : null}

      {/* --- Packdaten (Rueckmeldung vom Lager) ---------------------------- */}
      {type === 'delivery_note' && !document?.cancelledByDocumentId ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h2>Packen</h2>
              <div className="faint" style={{ fontSize: 12.5 }}>
                Rückmeldung vom Lager zur Packliste — danach Versandkosten als Position ergänzen und
                festschreiben. Die Versandrückmeldung mit Sendungsnummer übernimmt die Werte.
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="form-grid">
              <Field label="Packstücke" span={2}>
                <input
                  autoComplete="off"
                  className="input numeric"
                  type="number"
                  min={0}
                  max={9999}
                  value={header.packages || ''}
                  disabled={readOnly}
                  onChange={(event) => setHeader({ ...header, packages: Number(event.target.value) || 0 })}
                />
              </Field>
              <Field label="Gewicht (kg)" span={2}>
                <NumericInput
                  kind="quantity"
                  value={header.weightGrams}
                  onChange={(value) => setHeader({ ...header, weightGrams: value })}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Maße / Bemerkung des Lagers" span={8}>
                <TextInput
                  value={header.packNote}
                  onChange={(value) => setHeader({ ...header, packNote: value })}
                  placeholder="z. B. 2 Kartons 60×40×40, 1 Palette 120×80×110"
                  disabled={readOnly}
                />
              </Field>
            </div>
          </div>
        </div>
      ) : null}

      {/* --- Versandrueckmeldung ------------------------------------------- */}
      {!isNew && type === 'delivery_note' && document?.lifecycle === 'posted' && !document.cancelledByDocumentId ? (
        <ShipmentCard
          documentId={id!}
          documentNumber={document.number}
          defaultPackages={document.packages ?? undefined}
          defaultWeightGrams={document.weightGrams ?? undefined}
        />
      ) : null}

      {/* --- Dokumente ----------------------------------------------------- */}
      {!isNew ? (
        <DocumentAttachments
          documentId={id!}
          documentPosted={document?.lifecycle !== 'draft'}
        />
      ) : null}

      {/* --- Nachweis ----------------------------------------------------- */}
      {document?.lifecycle !== 'draft' && loaded?.integrity ? (
        <div className="card">
          <div className="card-body">
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <Badge kind={loaded.integrity.valid ? 'success' : 'danger'}>
                {loaded.integrity.valid ? 'Prüfsumme gültig' : 'Prüfsumme verletzt'}
              </Badge>
              <span className="faint mono" style={{ fontSize: 11.5 }}>
                SHA-256: {loaded.integrity.hash?.slice(0, 32)}…
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* --- Reservierung freigeben --------------------------------------- */}
      {showRelease ? (
        <Dialog
          title="Reservierung freigeben"
          onClose={() => setShowRelease(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setShowRelease(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={releaseReservation.isPending || !releaseReason.trim()}
                onClick={() => releaseReservation.mutate()}
              >
                Freigeben
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Die reservierte Menge dieses Auftrags wird freigegeben und steht anderen Aufträgen
            wieder zur Verfügung. Der Grund wird protokolliert.
          </p>
          <Field label="Grund" required>
            <TextArea value={releaseReason} onChange={setReleaseReason} rows={2} />
          </Field>
        </Dialog>
      ) : null}

      {/* --- Storno-Dialog ------------------------------------------------ */}
      {showCancel ? (
        <Dialog
          title={`${meta.label} stornieren`}
          onClose={() => setShowCancel(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setShowCancel(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={cancel.isPending || !cancelReason.trim()}
                onClick={() => cancel.mutate()}
              >
                Storno erstellen
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Es wird ein eigener Stornobeleg mit umgekehrten Beträgen und eigener Belegnummer
            erzeugt. {meta.label} {document?.number} bleibt vollständig erhalten und wird als
            storniert gekennzeichnet.
          </p>
          <Field label="Stornogrund" required error={errors.reason}>
            <TextArea value={cancelReason} onChange={setCancelReason} rows={2} />
          </Field>
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Chargen einer Wareneingangsposition erfassen.
 *
 * Chargenartikel: je Zeile Nummer, ggf. Haltbarkeit, Menge - die Summe muss
 * die Positionsmenge treffen. Seriennummern: eine je Zeile im Textfeld, jede
 * steht fuer ein Stueck. Der Server prueft dasselbe noch einmal.
 */
function LotEditor({
  line,
  readOnly,
  onChange,
}: {
  line: LineForm;
  readOnly: boolean;
  onChange: (lots: LotForm[]) => void;
}) {
  const tracking = line.tracking!;
  const sum = line.lots.reduce((total, lot) => total + lot.quantity, 0);
  const complete = sum === line.quantity;
  const label = tracking.mode === 'serial' ? 'Seriennummern' : 'Chargen';

  if (readOnly) {
    if (line.lots.length === 0) return <span className="faint">{label}: keine erfasst</span>;
    return (
      <div className="faint" style={{ fontSize: 12.5 }}>
        {label}:{' '}
        {line.lots
          .map((lot) =>
            [lot.lotNumber || 'ohne Nummer', lot.expiryDate ? `MHD ${formatDate(lot.expiryDate)}` : null, tracking.mode === 'serial' ? null : formatQuantity(lot.quantity)]
              .filter(Boolean)
              .join(' · '),
          )
          .join('; ')}
      </div>
    );
  }

  if (tracking.mode === 'serial') {
    const text = line.lots.map((lot) => lot.lotNumber).join('\n');
    return (
      <div className="stack" style={{ gap: 4 }}>
        <TextArea
          rows={Math.min(8, Math.max(2, line.lots.length + 1))}
          value={text}
          placeholder="Seriennummern, eine je Zeile"
          onChange={(value) =>
            onChange(
              value
                .split(/\r?\n/)
                .map((serial) => serial.trim())
                .filter(Boolean)
                .map((serial, index) => ({
                  key: line.lots[index]?.key ?? `sn-${index}-${serial}`,
                  lotId: null,
                  lotNumber: serial,
                  expiryDate: '',
                  quantity: 1000,
                })),
            )
          }
        />
        <span className="faint" style={{ fontSize: 12, color: complete ? undefined : 'var(--warning)' }}>
          {line.lots.length} Seriennummern erfasst, Position hat {formatQuantity(line.quantity)}
        </span>
      </div>
    );
  }

  const update = (key: string, changes: Partial<LotForm>) =>
    onChange(line.lots.map((lot) => (lot.key === key ? { ...lot, ...changes } : lot)));

  return (
    <div className="stack" style={{ gap: 6 }}>
      {line.lots.map((lot) => (
        <div key={lot.key} className="row" style={{ gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 2 }}>
            <TextInput
              value={lot.lotNumber}
              onChange={(value) => update(lot.key, { lotNumber: value })}
              placeholder="Chargennummer"
            />
          </div>
          {tracking.hasExpiry ? (
            <div style={{ flex: 1 }}>
              <TextInput type="date" value={lot.expiryDate} onChange={(value) => update(lot.key, { expiryDate: value })} />
            </div>
          ) : null}
          <div style={{ width: 120 }}>
            <NumericInput kind="quantity" value={lot.quantity} onChange={(value) => update(lot.key, { quantity: value })} />
          </div>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            title="Charge entfernen"
            onClick={() => onChange(line.lots.filter((entry) => entry.key !== lot.key))}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() =>
            onChange([
              ...line.lots,
              { key: `lot-${Date.now()}-${line.lots.length}`, lotId: null, lotNumber: '', expiryDate: '', quantity: line.quantity - sum > 0 ? line.quantity - sum : 0 },
            ])
          }
        >
          Charge hinzufügen
        </button>
        <span className="faint" style={{ fontSize: 12, color: complete ? undefined : 'var(--warning)' }}>
          {formatQuantity(sum)} von {formatQuantity(line.quantity)} zugeordnet{tracking.hasExpiry ? ' · Haltbarkeit je Charge' : ''}
        </span>
      </div>
    </div>
  );
}

type LotAvailability = {
  warehouseId: string;
  strategy: 'fifo' | 'fefo' | 'manual';
  lots: {
    id: string;
    lotNumber: string | null;
    expiryDate: string | null;
    receivedOn: string;
    sourceDocumentNumber: string | null;
    quantityRemaining: number;
  }[];
  proposal: { lotId: string | null; quantity: number }[];
  shortfall: number;
};

/**
 * Chargenauswahl am Lieferschein.
 *
 * Zeigt die offenen Zugaenge des Artikels im Lager des Belegs und ordnet die
 * Positionsmenge nach der Entnahmeregel zu (aelteste zuerst oder kuerzeste
 * Haltbarkeit zuerst). Der Vorschlag folgt Menge und Lager, bis die Auswahl
 * von Hand geaendert wird; "Nach Regel" stellt ihn wieder her. Bei Artikeln,
 * die von Hand kommissioniert werden, gibt es keinen Vorschlag. Das
 * Festschreiben prueft und ergaenzt die Zuordnung auf dem Server.
 */
function LotPicker({
  line,
  warehouseId,
  readOnly,
  onChange,
}: {
  line: LineForm;
  warehouseId: string;
  readOnly: boolean;
  onChange: (lots: LotForm[], manual: boolean) => void;
}) {
  const tracking = line.tracking!;
  const serial = tracking.mode === 'serial';
  const label = serial ? 'Seriennummern' : 'Chargen';
  const sum = line.lots.reduce((total, lot) => total + lot.quantity, 0);
  const complete = sum === line.quantity;

  const availability = useQuery({
    queryKey: ['lots-available', line.itemId, warehouseId, line.quantity],
    queryFn: () =>
      api.get<LotAvailability>(
        withQuery('/api/lots/available', {
          itemId: line.itemId,
          warehouseId: warehouseId || undefined,
          quantity: line.quantity,
        }),
      ),
    enabled: !readOnly && Boolean(line.itemId),
  });
  const data = availability.data;

  const toForms = (rows: { lotId: string | null; quantity: number }[]): LotForm[] =>
    rows.map((row) => {
      const lot = data?.lots.find((entry) => entry.id === row.lotId);
      return {
        key: row.lotId ?? 'rest',
        lotId: row.lotId,
        lotNumber: lot?.lotNumber ?? '',
        expiryDate: lot?.expiryDate ?? '',
        quantity: row.quantity,
      };
    });

  // Vorschlag uebernehmen, solange niemand von Hand eingegriffen hat.
  useEffect(() => {
    if (!data || readOnly || line.lotsManual || data.strategy === 'manual') return;
    const proposed = data.proposal.filter((row) => row.lotId);
    const same =
      proposed.length === line.lots.length &&
      proposed.every(
        (row, index) => row.lotId === line.lots[index]?.lotId && row.quantity === line.lots[index]?.quantity,
      );
    if (!same) onChange(toForms(proposed), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, readOnly, line.lotsManual]);

  if (readOnly) {
    if (line.lots.length === 0) return <span className="faint">{label}: keine zugeordnet</span>;
    return (
      <div className="faint" style={{ fontSize: 12.5 }}>
        {label}:{' '}
        {line.lots
          .map((lot) =>
            [
              lot.lotId ? lot.lotNumber || 'ohne Nummer' : 'Altbestand ohne Zugang',
              lot.expiryDate ? `MHD ${formatDate(lot.expiryDate)}` : null,
              serial ? null : formatQuantity(lot.quantity),
            ]
              .filter(Boolean)
              .join(' · '),
          )
          .join('; ')}
      </div>
    );
  }

  if (availability.isLoading) return <span className="faint">{label} werden geladen ...</span>;
  if (!data) return <span className="faint">{label}: Die Zugänge konnten nicht geladen werden.</span>;
  if (data.lots.length === 0) {
    return (
      <span className="faint" style={{ color: 'var(--warning)' }}>
        In diesem Lager sind keine {label} des Artikels vorhanden.
      </span>
    );
  }

  const chosen = (lotId: string) => line.lots.find((lot) => lot.lotId === lotId);
  const setQuantity = (lotId: string, quantity: number) => {
    const lot = data.lots.find((entry) => entry.id === lotId)!;
    const rest = line.lots.filter((entry) => entry.lotId !== lotId);
    const next =
      quantity > 0
        ? [...rest, { key: lotId, lotId, lotNumber: lot.lotNumber ?? '', expiryDate: lot.expiryDate ?? '', quantity }]
        : rest;
    // Reihenfolge wie in der Liste der Zugaenge halten.
    next.sort(
      (a, b) => data.lots.findIndex((l) => l.id === a.lotId) - data.lots.findIndex((l) => l.id === b.lotId),
    );
    onChange(next, true);
  };

  const rule =
    data.strategy === 'fefo'
      ? 'kürzeste Haltbarkeit zuerst'
      : data.strategy === 'fifo'
        ? 'ältester Zugang zuerst'
        : 'von Hand';

  return (
    <div className="stack" style={{ gap: 6 }}>
      <table className="lot-table" style={{ fontSize: 12.5, width: 'auto', minWidth: 520 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>{serial ? 'Seriennummer' : 'Charge'}</th>
            {tracking.hasExpiry ? <th style={{ textAlign: 'left' }}>MHD</th> : null}
            <th style={{ textAlign: 'left' }}>Zugang</th>
            <th style={{ textAlign: 'right' }}>verfügbar</th>
            <th style={{ textAlign: 'right' }}>{serial ? 'liefern' : 'entnehmen'}</th>
          </tr>
        </thead>
        <tbody>
          {data.lots.map((lot) => {
            const picked = chosen(lot.id);
            return (
              <tr key={lot.id} style={picked ? undefined : { opacity: 0.75 }}>
                <td className="mono">{lot.lotNumber ?? 'ohne Nummer'}</td>
                {tracking.hasExpiry ? <td>{lot.expiryDate ? formatDate(lot.expiryDate) : '-'}</td> : null}
                <td className="faint">
                  {formatDate(lot.receivedOn)}
                  {lot.sourceDocumentNumber ? ` · ${lot.sourceDocumentNumber}` : ''}
                </td>
                <td style={{ textAlign: 'right' }}>{formatQuantity(lot.quantityRemaining)}</td>
                <td style={{ textAlign: 'right', width: serial ? 60 : 120 }}>
                  {serial ? (
                    <input
                      type="checkbox"
                      checked={Boolean(picked)}
                      onChange={(event) => setQuantity(lot.id, event.target.checked ? 1000 : 0)}
                      aria-label={`Seriennummer ${lot.lotNumber ?? ''} liefern`}
                    />
                  ) : (
                    <NumericInput
                      kind="quantity"
                      value={picked?.quantity ?? 0}
                      onChange={(value) => setQuantity(lot.id, Math.min(value, lot.quantityRemaining))}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        {data.strategy !== 'manual' ? (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onChange(toForms(data.proposal.filter((row) => row.lotId)), false)}
          >
            Nach Regel ({rule})
          </button>
        ) : (
          <span className="faint" style={{ fontSize: 12 }}>
            Entnahmeregel: {rule}
          </span>
        )}
        <span className="faint" style={{ fontSize: 12, color: complete ? undefined : 'var(--warning)' }}>
          {formatQuantity(sum)} von {formatQuantity(line.quantity)} zugeordnet
          {line.lotsManual ? ' · von Hand geändert' : ''}
        </span>
      </div>
    </div>
  );
}
